import { gql, type TypedDocumentNode } from '@apollo/client';

import type {
  CompanyRegistration,
  SubmitCompanyRegistrationInput,
  UserError,
} from '../types/companyRegistration.types';

const REGISTRATION_PAYLOAD_FIELDS = `
  companyRegistration {
    id
    companyName
    companyCode
    industry
    size
    website
    country
    timezone
    applicantFirstName
    applicantLastName
    corporateEmail
    status
    emailVerifiedAt
    approvalComments
    approvedOn
    rejectedOn
    companyId
    createdAt
    updatedAt
  }
  errors {
    code
    message
  }
`;

type RegistrationPayload = { companyRegistration: CompanyRegistration | null; errors: UserError[] };

export const SUBMIT_COMPANY_REGISTRATION: TypedDocumentNode<
  { submitCompanyRegistration: RegistrationPayload },
  { input: SubmitCompanyRegistrationInput }
> = gql`
  mutation SubmitCompanyRegistration($input: SubmitCompanyRegistrationInput!) {
    submitCompanyRegistration(input: $input) { ${REGISTRATION_PAYLOAD_FIELDS} }
  }
`;

export const VERIFY_COMPANY_REGISTRATION: TypedDocumentNode<
  { verifyCompanyRegistration: RegistrationPayload },
  { input: { registrationId: string; otp: string } }
> = gql`
  mutation VerifyCompanyRegistration($input: VerifyCompanyRegistrationInput!) {
    verifyCompanyRegistration(input: $input) { ${REGISTRATION_PAYLOAD_FIELDS} }
  }
`;

export const RESEND_COMPANY_REGISTRATION_VERIFICATION: TypedDocumentNode<
  { resendCompanyRegistrationVerification: { errors: UserError[] } },
  // Exactly one key: the id when the verify screen has it, otherwise the corporate email for an
  // applicant who lost the link. The response never distinguishes outcomes (anti-enumeration), so a
  // caller learns nothing from it beyond "the request was well formed".
  { input: { registrationId?: string | null; email?: string | null } }
> = gql`
  mutation ResendCompanyRegistrationVerification($input: ResendCompanyRegistrationVerificationInput!) {
    resendCompanyRegistrationVerification(input: $input) {
      errors {
        code
        message
      }
    }
  }
`;

export const APPROVE_COMPANY_REGISTRATION: TypedDocumentNode<
  { approveCompanyRegistration: RegistrationPayload },
  { input: { registrationId: string; comments: string | null } }
> = gql`
  mutation ApproveCompanyRegistration($input: ApproveCompanyRegistrationInput!) {
    approveCompanyRegistration(input: $input) { ${REGISTRATION_PAYLOAD_FIELDS} }
  }
`;

export const REJECT_COMPANY_REGISTRATION: TypedDocumentNode<
  { rejectCompanyRegistration: RegistrationPayload },
  { input: { registrationId: string; comments: string } }
> = gql`
  mutation RejectCompanyRegistration($input: RejectCompanyRegistrationInput!) {
    rejectCompanyRegistration(input: $input) { ${REGISTRATION_PAYLOAD_FIELDS} }
  }
`;

export const BOOTSTRAP_COMPANY_REGISTRATION: TypedDocumentNode<
  { bootstrapCompanyRegistration: RegistrationPayload },
  { input: { registrationId: string } }
> = gql`
  mutation BootstrapCompanyRegistration($input: BootstrapCompanyRegistrationInput!) {
    bootstrapCompanyRegistration(input: $input) { ${REGISTRATION_PAYLOAD_FIELDS} }
  }
`;
